import React, { useMemo } from "react";
import {
  Paper,
  Typography,
  Stack,
  alpha,
  useTheme,
  Box,
  Tooltip,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatAmount,
  formatDashedDateWithYear,
  formatHistoryAmount,
} from "../../utils/hooks/dataHandler";
import DetailsContainer from "./utils/DetailsContainer";
import DetailsTable from "./utils/DetailsTable";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import CustomDatePicker from "../utils/DatePicker/CustomDatePicker";
import { CalculatorTextField } from "../utils/TextField/NumericTextField";
import { handleAmountFocus } from "../../utils/hooks/eventMonitor";
import { JustInfo } from "../utils/Icon";
import { TextFieldForViewOnly } from "../utils/TextField/TextField";

const TaxPreviouslyAssessedField = ({
  label,
  children,
  showInfo = false,
  sx,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.2),
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        flex: 1,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{
          fontWeight: 500,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
        }}
      >
        {label}

        {showInfo && (
          <JustInfo title="ECI is an estimate of your company's taxable profits (after deducting tax-allowable expenses) for a Year of Assessment (YA)." />
        )}
      </Typography>
      {children}
    </Paper>
  );
};

const getCalculatorValue = (item, historyField, amountField) => {
  const historyValue = item?.[historyField];
  return Array.isArray(historyValue) && historyValue.length > 0
    ? historyValue
    : [item?.[amountField] || 0];
};

const sumCalculatorValues = (value) =>
  Array.isArray(value)
    ? value.reduce((sum, num) => sum + (parseFloat(num) || 0), 0)
    : parseFloat(value) || 0;

const TaxPreviouslyAssessedAmountField = ({
  disabled,
  canEdit,
  item,
  label,
  amountField,
  historyField,
  onUpdate,
  onValueChange,
}) => (
  <TaxPreviouslyAssessedField label={label}>
    <Tooltip
      title={
        item?.[historyField] &&
        item[historyField].length > 0 &&
        formatHistoryAmount(item[historyField])
      }
      placement="top"
    >
      <Box sx={{ width: "100%" }}>
        {canEdit ? (
          <CalculatorTextField
            allowDecimal
            value={getCalculatorValue(item, historyField, amountField)}
            onChange={(value) =>
              onValueChange
                ? onValueChange(value)
                : onUpdate(amountField, value, 0)
            }
            reference={{ field: amountField }}
            needRound={false}
            sx={{
              mt: 2,
            }}
            disabled={disabled}
            canEdit={canEdit}
          />
        ) : (
          <TextFieldForViewOnly
            value={formatAmount(item?.[amountField] || 0) || ""}
          />
        )}
      </Box>
    </Tooltip>
  </TaxPreviouslyAssessedField>
);

const isAutoloadRow = (row) => row?.status === "autoload";

const getNumericAmount = (value) => parseFloat(value) || 0;

const hasCrossYAValue = (row, amountField, historyField) =>
  getNumericAmount(row?.[amountField]) !== 0 ||
  (Array.isArray(row?.[historyField]) && row[historyField].length > 0);

const getCrossYAItem = (manualRows, amountField, historyField, legacyIndex) => {
  const matchingRow =
    manualRows.find((row) => getNumericAmount(row?.[amountField]) !== 0) ||
    manualRows.find((row) => hasCrossYAValue(row, amountField, historyField));
  if (matchingRow) return matchingRow;

  // Only use row order for legacy records with no values identifying either YA.
  const legacyRow =
    manualRows[legacyIndex] ||
    (manualRows.length === 1 ? manualRows[0] : undefined);
  return legacyRow &&
    !hasCrossYAValue(legacyRow, "preceding_amount", "precedingHistoryAmount") &&
    !hasCrossYAValue(legacyRow, "current_amount", "currentHistoryAmount")
    ? legacyRow
    : {};
};

const getDateKey = (date) => {
  if (!date) return "";
  const parsedDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return parsedDate.toISOString().slice(0, 10);
};

const shouldKeepManualRow = (row) =>
  row?.date ||
  getNumericAmount(row?.preceding_amount) !== 0 ||
  getNumericAmount(row?.current_amount) !== 0 ||
  (Array.isArray(row?.precedingHistoryAmount) &&
    row.precedingHistoryAmount.length > 0) ||
  (Array.isArray(row?.currentHistoryAmount) &&
    row.currentHistoryAmount.length > 0);

const buildCrossYATaxPreviouslyAssessedRows = ({
  baseRow,
  currentDate,
  currentHistoryAmount,
  currentAmount,
  precedingDate,
  precedingHistoryAmount,
  precedingAmount,
}) => {
  const precedingDateKey = getDateKey(precedingDate);
  const currentDateKey = getDateKey(currentDate);
  const shouldMerge = precedingDateKey && precedingDateKey === currentDateKey;

  if (shouldMerge) {
    return [
      {
        ...baseRow,
        date: precedingDate || currentDate || "",
        preceding_amount: precedingAmount,
        current_amount: currentAmount,
        precedingHistoryAmount,
        currentHistoryAmount,
        amount: precedingAmount + currentAmount,
      },
    ];
  }

  const rows = [
    {
      ...baseRow,
      date: precedingDate || "",
      preceding_amount: precedingAmount,
      current_amount: 0,
      precedingHistoryAmount,
      currentHistoryAmount: [],
      amount: precedingAmount,
    },
    {
      ...baseRow,
      date: currentDate || "",
      preceding_amount: 0,
      current_amount: currentAmount,
      precedingHistoryAmount: [],
      currentHistoryAmount,
      amount: currentAmount,
    },
  ];

  const visibleRows = rows.filter(shouldKeepManualRow);
  return visibleRows.length > 0 ? visibleRows : rows;
};

const TaxPreviouslyAssessed = ({
  id,
  dataId,
  data,
  totalAmount,
  onUpdate,
  onReplaceRows,
  disabled = false,
  canEdit = true,
  currency,
  currencyRate,
  isCrossYA = false,
}) => {
  const theme = useTheme();

  const manualRows = useMemo(
    () =>
      Array.isArray(data) ? data.filter((row) => !isAutoloadRow(row)) : [],
    [data],
  );

  const item = useMemo(() => manualRows[0] || {}, [manualRows]);

  const precedingItem = useMemo(
    () =>
      getCrossYAItem(manualRows, "preceding_amount", "precedingHistoryAmount", 0),
    [manualRows],
  );

  const currentItem = useMemo(
    () =>
      getCrossYAItem(manualRows, "current_amount", "currentHistoryAmount", 1),
    [manualRows],
  );

  const updateCrossYATaxPreviouslyAssessed = (updates) => {
    const precedingHistoryAmount =
      updates.precedingHistoryAmount ??
      getCalculatorValue(
        precedingItem,
        "precedingHistoryAmount",
        "preceding_amount",
      );
    const currentHistoryAmount =
      updates.currentHistoryAmount ??
      getCalculatorValue(currentItem, "currentHistoryAmount", "current_amount");
    const precedingAmount =
      updates.precedingAmount ?? sumCalculatorValues(precedingHistoryAmount);
    const currentAmount =
      updates.currentAmount ?? sumCalculatorValues(currentHistoryAmount);
    const precedingDate = Object.prototype.hasOwnProperty.call(
      updates,
      "precedingDate",
    )
      ? updates.precedingDate
      : precedingItem.date || null;
    const currentDate = Object.prototype.hasOwnProperty.call(
      updates,
      "currentDate",
    )
      ? updates.currentDate
      : currentItem.date || null;

    const rows = buildCrossYATaxPreviouslyAssessedRows({
      baseRow: item,
      precedingDate,
      currentDate,
      precedingAmount,
      currentAmount,
      precedingHistoryAmount,
      currentHistoryAmount,
    });

    onReplaceRows?.(rows);
  };

  const rows = useMemo(
    () =>
      Array.isArray(data)
        ? data
            .map((row, index) => ({ ...row, __sourceIndex: index }))
            .filter((d) => d.status === "autoload")
        : [],
    [data],
  );

  return (
    <DetailsContainer
      id={id}
      title="Tax Deductions"
      totalAmount={totalAmount}
      icon={AssuredWorkloadIcon}
      showDecimal
    >
      <Box sx={{ p: 3 }}>
        {/* Section 1: Tax Previously Assessed */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: theme.palette.text.primary,
              borderBottom: `2px solid ${alpha(
                theme.palette.primary.main,
                0.3,
              )}`,
              pb: 1,
            }}
          >
            Tax Previously Assessed
          </Typography>

          {isCrossYA ? (
            <Stack spacing={3}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                <TaxPreviouslyAssessedField
                  label="Preceding ECI Date (dd-mm-yyyy)"
                  showInfo
                >
                  {canEdit ? (
                    <CustomDatePicker
                      selected={precedingItem.date || null}
                      onChange={(value) =>
                        updateCrossYATaxPreviouslyAssessed({
                          precedingDate: value,
                        })
                      }
                      label=""
                      data={data}
                      index={0}
                      handleAmountFocus={handleAmountFocus}
                      sx={{
                        mt: 2,
                      }}
                      disabled={disabled}
                    />
                  ) : (
                    <TextFieldForViewOnly
                      value={formatDashedDateWithYear(precedingItem.date) || ""}
                    />
                  )}
                </TaxPreviouslyAssessedField>
                <TaxPreviouslyAssessedAmountField
                  label="Preceding Amount"
                  amountField="preceding_amount"
                  historyField="precedingHistoryAmount"
                  item={precedingItem}
                  disabled={disabled}
                  canEdit={canEdit}
                  onValueChange={(value) =>
                    updateCrossYATaxPreviouslyAssessed({
                      precedingHistoryAmount: value.length > 0 ? value : [0],
                      precedingAmount: sumCalculatorValues(value),
                    })
                  }
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                <TaxPreviouslyAssessedField label="Current ECI Date (dd-mm-yyyy)">
                  {canEdit ? (
                    <CustomDatePicker
                      selected={currentItem.date || null}
                      onChange={(value) =>
                        updateCrossYATaxPreviouslyAssessed({
                          currentDate: value,
                        })
                      }
                      label=""
                      data={data}
                      index={1}
                      handleAmountFocus={handleAmountFocus}
                      sx={{
                        mt: 2,
                      }}
                      disabled={disabled}
                    />
                  ) : (
                    <TextFieldForViewOnly
                      value={
                        formatDashedDateWithYear(currentItem.date) || ""
                      }
                    />
                  )}
                </TaxPreviouslyAssessedField>
                <TaxPreviouslyAssessedAmountField
                  label="Current Amount"
                  amountField="current_amount"
                  historyField="currentHistoryAmount"
                  item={currentItem}
                  disabled={disabled}
                  canEdit={canEdit}
                  onValueChange={(value) =>
                    updateCrossYATaxPreviouslyAssessed({
                      currentHistoryAmount: value.length > 0 ? value : [0],
                      currentAmount: sumCalculatorValues(value),
                    })
                  }
                />
              </Stack>

            </Stack>
          ) : (
            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <TaxPreviouslyAssessedField
                label="ECI Date (dd-mm-yyyy)"
                showInfo
              >
                {canEdit ? (
                  <CustomDatePicker
                    selected={item.date || null}
                    onChange={(value) => onUpdate("date", value, 0)}
                    label=""
                    data={data}
                    index={0}
                    handleAmountFocus={handleAmountFocus}
                    sx={{
                      mt: 2,
                    }}
                    disabled={disabled}
                  />
                ) : (
                  <TextFieldForViewOnly
                    value={formatDashedDateWithYear(item.date) || ""}
                  />
                )}
              </TaxPreviouslyAssessedField>
              <TaxPreviouslyAssessedAmountField
                label="Amount"
                amountField="amount"
                historyField="historyAmount"
                item={item}
                disabled={disabled}
                canEdit={canEdit}
                onUpdate={onUpdate}
              />
            </Stack>
          )}
        </Box>

        {/* Section 2: Tax Deductions */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: theme.palette.text.primary,
              borderBottom: `2px solid ${alpha(
                theme.palette.secondary.main,
                0.3,
              )}`,
              pb: 1,
            }}
          >
            Tax Set Off
          </Typography>

          {rows && rows.length > 0 ? (
            <DetailsTable
              dataId={dataId}
              data={rows}
              noRowsHint="No tax previously assessed details available."
              maxHeight={400}
              onUpdateReference={(dataId, updates, index) => {
                if (onUpdate) {
                  if (
                    Object.prototype.hasOwnProperty.call(
                      updates,
                      "crossYAOffset",
                    )
                  ) {
                    onUpdate(
                      "crossYAOffset",
                      updates.crossYAOffset,
                      rows[index].__sourceIndex,
                    );
                    return;
                  }

                  onUpdate(
                    "reference",
                    updates.reference,
                    rows[index].__sourceIndex,
                  );
                }
              }}
              showDecimal
              canEdit={!disabled}
              currency={currency}
              currencyRate={currencyRate}
              isCrossYA={isCrossYA}
            />
          ) : (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
                bgcolor: alpha(theme.palette.grey[100], 0.5),
                borderRadius: 1,
                border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              No tax deductions available
            </Box>
          )}
        </Box>
      </Box>
    </DetailsContainer>
  );
};

export default TaxPreviouslyAssessed;
